import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  getAvailabilitySettings,
  getBlockedDates,
  getAvailableTimeSlots,
  isDateFullyBlocked,
  fetchBookedSlotsForDate,
} from '../services/availabilityService';
import { getBookingWeekday } from '../utils/bookingDates';
import { pagePadding } from '../styles/layout';
import {
  GOLD_RGBA,
  PANEL_BG,
  MIN_BOOKING_HOURS,
  DURATION_OPTIONS,
  BOOKING_PACKAGES,
  BOOKING_BG,
  calculateBookingPricing,
  getIncludedAddOns,
  getPackageById,
  getPackageMinHours,
} from '../constants/booking';
import { generateBookingHours, isTimeRangeAvailable } from '../components/booking/bookingHelpers';
import PackageSelector from '../components/booking/PackageSelector';
import BookingCalendar from '../components/booking/BookingCalendar';
import TimeSlotPicker from '../components/booking/TimeSlotPicker';
import DurationPicker from '../components/booking/DurationPicker';
import AddOnSelector from '../components/booking/AddOnSelector';
import PriceSummary from '../components/booking/PriceSummary';
import CheckoutFooter from '../components/booking/CheckoutFooter';

const MIN_HOURS = MIN_BOOKING_HOURS;

const columnPanelStyle = {
  backgroundColor: PANEL_BG,
  borderRadius: '1rem',
  padding: '2rem',
  border: `1px solid ${GOLD_RGBA}, 0.25)`,
  display: 'flex',
  flexDirection: 'column',
};

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(() =>
    getPackageById(searchParams.get('package'))
  );
  const [durationHours, setDurationHours] = useState(MIN_BOOKING_HOURS);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [loadingBookedTimes, setLoadingBookedTimes] = useState(false);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tentativeBookingHours, setTentativeBookingHours] = useState([]);
  const [availabilitySettings, setAvailabilitySettings] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [existingBookings, setExistingBookings] = useState([]);

  const includedAddOns = useMemo(() => getIncludedAddOns(selectedPackage), [selectedPackage]);
  const pricing = useMemo(
    () => calculateBookingPricing(selectedPackage, durationHours, []),
    [selectedPackage, durationHours]
  );

  const minHoursForPackage = getPackageMinHours(selectedPackage);
  const isFlatRate = !!selectedPackage?.flatRate;
  const durationOptions = isFlatRate
    ? [getPackageMinHours(selectedPackage)]
    : DURATION_OPTIONS;
  const showCheckout = !!(selectedPackage && selectedDate && selectedTime);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [settings, blocked] = await Promise.all([getAvailabilitySettings(), getBlockedDates()]);
        setAvailabilitySettings(settings);
        setBlockedDates(blocked);
      } catch (err) {
        console.error('Error loading availability settings:', err);
        setError('Failed to load availability settings. Please refresh the page.');
      }
    })();
  }, []);

  const isDayAvailable = useCallback(
    (date) => {
      if (!date || !availabilitySettings) return false;
      if (isDateFullyBlocked(date, blockedDates)) return false;
      const dayName = getBookingWeekday(date);
      const schedule = availabilitySettings.weeklySchedule[dayName];
      if (!schedule || !schedule.enabled) return false;
      return getAvailableTimeSlots(date, availabilitySettings.weeklySchedule, blockedDates, availabilitySettings.generalSettings).length > 0;
    },
    [availabilitySettings, blockedDates]
  );

  const getMaxAvailableHoursForTime = useCallback(
    (startTime) => {
      if (!startTime) return DURATION_OPTIONS[DURATION_OPTIONS.length - 1];
      let maxHours = MIN_HOURS;
      for (let h = MIN_HOURS; h <= DURATION_OPTIONS[DURATION_OPTIONS.length - 1]; h++) {
        if (isTimeRangeAvailable(startTime, h, bookedTimes)) maxHours = h;
        else break;
      }
      return maxHours;
    },
    [bookedTimes]
  );

  const fetchBookedTimes = async (date) => {
    if (!date) return;
    setLoadingBookedTimes(true);
    setError(null);
    try {
      const fullBookings = await fetchBookedSlotsForDate(date);
      const bookedList = [];
      fullBookings.forEach((data) => {
        bookedList.push(...generateBookingHours(data.time, data.totalHours));
      });
      setBookedTimes(bookedList);
      setExistingBookings(fullBookings);
    } catch (err) {
      console.error('Error fetching booked times:', err);
      setError('Failed to load available times. Please try again.');
    } finally {
      setLoadingBookedTimes(false);
    }
  };

  // Re-fetch when date changes
  useEffect(() => {
    if (selectedDate) fetchBookedTimes(selectedDate);
  }, [selectedDate]);

  // Update available time slots when date/settings change
  useEffect(() => {
    if (selectedDate && availabilitySettings) {
      setAvailableTimeSlots(
        getAvailableTimeSlots(selectedDate, availabilitySettings.weeklySchedule, blockedDates, availabilitySettings.generalSettings)
      );
    } else {
      setAvailableTimeSlots([]);
    }
  }, [selectedDate, availabilitySettings, blockedDates]);

  // Clear selected date if it becomes unavailable (e.g. admin blocks it)
  useEffect(() => {
    if (!selectedDate || !availabilitySettings) return;
    if (!isDayAvailable(selectedDate)) {
      setSelectedDate(null);
      setSelectedTime(null);
      setAvailableTimeSlots([]);
      setBookedTimes([]);
      setError('The selected date is no longer available. Please choose another date.');
    }
  }, [blockedDates, selectedDate, availabilitySettings, isDayAvailable]);

  // Auto-select nearest available date when package is chosen
  useEffect(() => {
    if (!selectedPackage || !availabilitySettings || selectedDate) return;
    let checkDate = new Date();
    checkDate.setDate(checkDate.getDate() + 1);
    for (let i = 0; i < 60; i++) {
      if (isDayAvailable(checkDate)) {
        setSelectedDate(checkDate);
        setCurrentMonth(new Date(checkDate.getFullYear(), checkDate.getMonth(), 1));
        break;
      }
      checkDate = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate() + 1);
    }
  }, [selectedPackage, availabilitySettings, blockedDates, selectedDate, isDayAvailable]);

  // Keep tentative booking hours in sync
  useEffect(() => {
    if (selectedTime && selectedDate) {
      setTentativeBookingHours(generateBookingHours(selectedTime, durationHours));
    } else {
      setTentativeBookingHours([]);
    }
  }, [selectedTime, durationHours, selectedDate]);

  // --- Handlers ---

  const handlePackageSelect = (pkg) => {
    if (!pkg) {
      setSelectedPackage(null);
      setDurationHours(MIN_HOURS);
      return;
    }
    setSelectedPackage(pkg);
    if (pkg.flatRate) setDurationHours(pkg.fixedDuration || getPackageMinHours(pkg));
    else if (durationHours < MIN_HOURS) setDurationHours(MIN_HOURS);
  };

  const handleUpgradePackage = (packageId) => {
    const pkg = getPackageById(packageId);
    if (pkg) handlePackageSelect(pkg);
  };

  const handleDateSelect = (date) => {
    if (!isDayAvailable(date)) return;
    setSelectedDate(date);
    setSelectedTime(null);
    setAvailableTimeSlots([]);
    fetchBookedTimes(date);
  };

  const handleDurationSelect = (hours) => {
    if (!selectedTime) { setDurationHours(hours); return; }
    if (!isTimeRangeAvailable(selectedTime, hours, bookedTimes)) {
      setError('That duration conflicts with an existing booking for this start time.');
      return;
    }
    if (hours > getMaxAvailableHoursForTime(selectedTime)) {
      setError(`Only ${getMaxAvailableHoursForTime(selectedTime)} consecutive hours are available from ${selectedTime}.`);
      return;
    }
    setError(null);
    setDurationHours(hours);
  };

  const navigateToCustomerInfo = () => {
    if (!selectedPackage) return;
    const minHours = getPackageMinHours(selectedPackage);
    navigate('/customer-info', {
      state: {
        bookingData: {
          selectedPackage: {
            id: selectedPackage.id,
            name: selectedPackage.name,
            basePrice: selectedPackage.basePrice,
            hourlyRate: selectedPackage.hourlyRate,
            baseHours: minHours,
            fixedDuration: selectedPackage.fixedDuration,
            flatRate: selectedPackage.flatRate,
          },
          selectedAddOns: includedAddOns,
          selectedDate,
          selectedTime,
          extraHours: Math.max(0, durationHours - minHours),
          durationHours,
        },
      },
    });
  };

  const handleBookNow = () => {
    if (!selectedPackage) { setError('Please select a package before proceeding.'); return; }
    if (!selectedDate || !selectedTime) { setError('Please select a date and time before proceeding.'); return; }
    if (isDateFullyBlocked(selectedDate, blockedDates)) { setError('This date is blocked and cannot be booked. Please choose another date.'); return; }
    if (!availableTimeSlots.includes(selectedTime)) { setError('The selected time is not available. Please choose another time.'); return; }
    if (!isTimeRangeAvailable(selectedTime, durationHours, bookedTimes)) { setError('Selected time range conflicts with existing bookings. Please choose another time.'); return; }
    setError(null);
    navigateToCustomerInfo();
  };

  // --- Loading state ---
  if (!availabilitySettings) {
    return (
      <div className="dd-page dd-booking" style={{ padding: pagePadding(isMobile), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#111111', borderRadius: '1rem', padding: '2rem', textAlign: 'center', border: `1px solid ${GOLD_RGBA}, 0.25)` }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#F5F0E8' }}>
            Loading Booking System...
          </h2>
          <p style={{ color: '#F5F0E8' }}>Please wait while we load availability settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dd-page dd-booking" style={{ backgroundColor: BOOKING_BG, padding: pagePadding(isMobile) }}>
      <Helmet>
        <title>Book a Golf Simulator | Digital Divot Golf — Phoenix &amp; Scottsdale</title>
        <meta
          name="description"
          content="Reserve your premium mobile golf simulator. Choose your package, date, and time — delivered anywhere in the Phoenix & Scottsdale metro area."
        />
        <meta property="og:title" content="Book a Golf Simulator | Digital Divot Golf" />
        <meta property="og:description" content="Reserve your premium mobile golf simulator in Phoenix & Scottsdale." />
        <meta property="og:image" content="https://digitaldivotgolf.com/HomepageAI_hero.webp" />
        <meta property="og:url" content="https://digitaldivotgolf.com/booking" />
      </Helmet>
      <div className="dd-booking-content">
        <div className="dd-page-intro">
          <h1>Book Your Simulator</h1>
          <p>Premium mobile golf simulator — delivered to your venue.</p>
        </div>

        <PackageSelector
          packages={BOOKING_PACKAGES}
          selectedPackage={selectedPackage}
          onSelect={handlePackageSelect}
          isMobile={isMobile}
        />

        <div className={`dd-booking-details${selectedPackage ? ' dd-booking-details--open' : ''}`} aria-hidden={!selectedPackage}>
          <div className="dd-booking-details-inner">
            {selectedPackage && (
              <div className="dd-booking-layout">
                {/* Left panel — calendar, time, duration */}
                <div className="dd-booking-panel" style={{ ...columnPanelStyle, height: 'auto' }}>
                  <BookingCalendar
                    currentMonth={currentMonth}
                    selectedDate={selectedDate}
                    isDayAvailable={isDayAvailable}
                    onDateSelect={handleDateSelect}
                    onPrevMonth={() => setCurrentMonth((prev) => { const d = new Date(prev); d.setMonth(prev.getMonth() - 1); return d; })}
                    onNextMonth={() => setCurrentMonth((prev) => { const d = new Date(prev); d.setMonth(prev.getMonth() + 1); return d; })}
                    isMobile={isMobile}
                  />
                  <TimeSlotPicker
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    availableTimeSlots={availableTimeSlots}
                    bookedTimes={bookedTimes}
                    durationHours={durationHours}
                    tentativeBookingHours={tentativeBookingHours}
                    existingBookings={existingBookings}
                    availabilitySettings={availabilitySettings}
                    loadingBookedTimes={loadingBookedTimes}
                    onTimeSelect={setSelectedTime}
                    isMobile={isMobile}
                  />
                  <DurationPicker
                    durationOptions={durationOptions}
                    durationHours={durationHours}
                    minHoursForPackage={minHoursForPackage}
                    selectedPackage={selectedPackage}
                    selectedTime={selectedTime}
                    tentativeBookingHours={tentativeBookingHours}
                    bookedTimes={bookedTimes}
                    getMaxAvailableHoursForTime={getMaxAvailableHoursForTime}
                    onDurationSelect={handleDurationSelect}
                    isMobile={isMobile}
                  />
                </div>

                {/* Right panel — add-ons, price summary, checkout */}
                <div className="dd-booking-panel" style={{ ...columnPanelStyle, height: 'auto' }}>
                  <AddOnSelector
                    selectedPackage={selectedPackage}
                    includedAddOns={includedAddOns}
                    onUpgradePackage={handleUpgradePackage}
                  />
                  <PriceSummary
                    pricing={pricing}
                    selectedPackage={selectedPackage}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    durationHours={durationHours}
                    selectedAddOns={[]}
                    includedAddOns={includedAddOns}
                  />
                  <CheckoutFooter
                    showCheckout={showCheckout}
                    error={error}
                    onBookNow={handleBookNow}
                    pricing={pricing}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
